import * as React from 'react';

import { useContainerWidth } from '../../hooks/useContainerWidth';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { cx } from '../../utils/cx';
import Carousel, { useCarousel } from '../base/Carousel';
import LoadingIndicator from '../base/LoadingIndicator';
import { PaginationDot } from '../base/pagination/PaginationDot';
import CalloutCard, { CALLOUT_CARD_HEIGHT } from '../CalloutCard';

import type { Callout } from '../CalloutCard';

interface ICalloutCarouselProps {
  callouts: Callout[];
  onDismiss?: (calloutId: string, appropriate?: boolean) => void;
  className?: string;
  onLoadMore?: () => void | Promise<void>;
  showLoadMore?: boolean;
  isLoadingMore?: boolean;
  onCalloutClick?: (callout: Callout) => void;
}

const CARD_GAP = 24;
const EDGE_PADDING = 32; // matches Entity.Body px-8 padding
const MIN_CARD_WIDTH = 300;
const MAX_VISIBLE_CARDS = 3;

interface ICarouselIndicatorProps {
  className?: string;
  showLoadMore?: boolean;
}

const CarouselIndicator = ({ className, showLoadMore = false }: ICarouselIndicatorProps) => {
  const { selectedIndex, scrollSnaps, api } = useCarousel();

  const totalPages = scrollSnaps.length + (showLoadMore ? 1 : 0);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={className}>
      <PaginationDot
        size="md"
        page={selectedIndex + 1}
        total={totalPages}
        onPageChange={(page) => {
          if (showLoadMore && page === totalPages) {
            return;
          }
          api?.scrollTo(page - 1);
        }}
      />
    </div>
  );
};

interface ICalloutCarouselContentProps {
  onDismiss?: (calloutId: string, appropriate?: boolean) => void;
  onLoadMore?: () => void | Promise<void>;
  showLoadMore: boolean;
  isLoadingMore: boolean;
  pages: Callout[][];
  containerWidth: number;
  edgePadding: number;
  cardGap: number;
  /** Returns card width for a given page based on number of cards on that page (allows 2 cards to fill full width) */
  getCardWidthForPage: (cardsOnPage: number) => number;
  onCalloutClick?: (callout: Callout) => void;
}

const CalloutCarouselContent = ({
  onDismiss,
  onLoadMore,
  showLoadMore,
  isLoadingMore,
  pages,
  containerWidth,
  edgePadding,
  cardGap,
  getCardWidthForPage,
  onCalloutClick,
}: ICalloutCarouselContentProps) => {
  const { canScrollNext, api, selectedIndex, scrollSnaps } = useCarousel();
  const [isLoading, setIsLoading] = React.useState(false);
  const previousPagesLengthRef = React.useRef(pages.length);
  const userTriggeredLoadRef = React.useRef(false);

  const isAtEnd = selectedIndex === scrollSnaps.length - 1;
  const shouldLoadMore = showLoadMore && isAtEnd && !canScrollNext && onLoadMore;

  // Handle loading more when clicking next at the end
  const handleNextClick = React.useCallback(
    async (e: React.MouseEvent) => {
      if (shouldLoadMore && !isLoading && !isLoadingMore) {
        e.preventDefault();
        e.stopPropagation();
        setIsLoading(true);
        userTriggeredLoadRef.current = true;
        try {
          await onLoadMore?.();
        } finally {
          setIsLoading(false);
        }
      }
    },
    [shouldLoadMore, onLoadMore, isLoading, isLoadingMore]
  );

  // Scroll to new content when pages are added via user-triggered load more
  React.useEffect(() => {
    if (pages.length > previousPagesLengthRef.current && api && userTriggeredLoadRef.current) {
      const newPageIndex = previousPagesLengthRef.current;
      setTimeout(() => {
        api.scrollTo(newPageIndex);
      }, 0);
      userTriggeredLoadRef.current = false;
    }
    previousPagesLengthRef.current = pages.length;
  }, [pages.length, api]);

  const isNextButtonLoading = isLoading || isLoadingMore;

  return (
    <>
      <div className="relative w-full">
        <Carousel.PrevTrigger
          className={({ isDisabled }) =>
            cx(
              'absolute top-1/2 z-10 flex size-6 shrink-0 -translate-y-1/2 items-center justify-center rounded-md transition',
              'left-1',
              isDisabled ? 'invisible' : 'hover:bg-primary_hover'
            )
          }
          aria-label="Previous callouts"
        >
          <ChevronLeftIcon className="text-tertiary size-4" />
        </Carousel.PrevTrigger>

        <div className="w-full">
          <Carousel.Content className="gap-0">
            {pages.map((pageCallouts, pageIndex) => (
              <Carousel.Item key={pageIndex} className="shrink-0" style={{ width: containerWidth }}>
                <div style={{ paddingLeft: edgePadding, paddingRight: edgePadding }}>
                  <div className="flex" style={{ gap: cardGap }}>
                    {pageCallouts.map((callout) => (
                      <div
                        key={callout.id}
                        className="shrink-0"
                        style={{
                          width: getCardWidthForPage(pageCallouts.length),
                          height: CALLOUT_CARD_HEIGHT,
                        }}
                      >
                        <CalloutCard
                          callout={callout}
                          onDismiss={
                            onDismiss ? (appropriate?: boolean) => onDismiss(callout.id, appropriate) : undefined
                          }
                          onClick={onCalloutClick ? () => onCalloutClick(callout) : undefined}
                          className="h-full w-full"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Carousel.Item>
            ))}
          </Carousel.Content>
        </div>

        {shouldLoadMore ? (
          <button
            onClick={handleNextClick}
            className={cx(
              'absolute top-1/2 z-10 flex size-6 shrink-0 -translate-y-1/2 items-center justify-center rounded-md transition',
              'right-1',
              isNextButtonLoading ? 'pointer-events-none' : 'hover:bg-primary_hover'
            )}
            aria-label="Next callouts"
            disabled={isNextButtonLoading}
          >
            {isNextButtonLoading ? (
              <LoadingIndicator size="sm" />
            ) : (
              <ChevronRightIcon className="text-tertiary size-4" />
            )}
          </button>
        ) : (
          <Carousel.NextTrigger
            className={({ isDisabled }) =>
              cx(
                'absolute top-1/2 z-10 flex size-6 shrink-0 -translate-y-1/2 items-center justify-center rounded-md transition',
                'right-1',
                isDisabled ? 'invisible' : 'hover:bg-primary_hover'
              )
            }
            aria-label="Next callouts"
          >
            <ChevronRightIcon className="text-tertiary size-4" />
          </Carousel.NextTrigger>
        )}
      </div>

      <CarouselIndicator showLoadMore={showLoadMore} />
    </>
  );
};

const CalloutCarouselBase = ({
  callouts,
  onDismiss,
  className,
  onLoadMore,
  showLoadMore = false,
  isLoadingMore = false,
  onCalloutClick,
}: ICalloutCarouselProps) => {
  const { containerWidth: measuredWidth, containerRef } = useContainerWidth<HTMLDivElement>();

  // Calculate how many cards can fit based on container width
  const containerWidth = measuredWidth;
  const availableWidth = containerWidth - EDGE_PADDING * 2;
  const calculatedCards = Math.floor((availableWidth + CARD_GAP) / (MIN_CARD_WIDTH + CARD_GAP));
  const visibleCards = Math.max(1, Math.min(MAX_VISIBLE_CARDS, calculatedCards));

  // Calculate card width for a page.
  // When all callouts fit on a single page, size cards to fill the full width.
  // When there are multiple pages, use consistent visibleCards-based width so cards
  // don't change size as you scroll (e.g. a last page with 2 of 3 cards).
  const isSinglePage = callouts.length <= visibleCards;
  const getCardWidthForPage = (cardsOnPage: number) => {
    const effectiveCount = isSinglePage ? cardsOnPage : visibleCards;
    return (availableWidth - (effectiveCount - 1) * CARD_GAP) / effectiveCount;
  };

  // Group callouts into pages
  const pages: Callout[][] = React.useMemo(() => {
    const result: Callout[][] = [];
    for (let i = 0; i < callouts.length; i += visibleCards) {
      result.push(callouts.slice(i, i + visibleCards));
    }
    return result;
  }, [callouts, visibleCards]);

  // Auto-fetch more when last page is partial and more data exists
  React.useEffect(() => {
    if (!showLoadMore || isLoadingMore || !onLoadMore) {
      return;
    }

    const lastPage = pages[pages.length - 1];
    const isLastPagePartial = lastPage && lastPage.length < visibleCards;

    if (isLastPagePartial) {
      void onLoadMore();
    }
  }, [pages, visibleCards, showLoadMore, isLoadingMore, onLoadMore]);

  if (callouts.length === 0) {
    return null;
  }

  const hasValidWidth = measuredWidth > 0;

  return (
    <div ref={containerRef} className="-mx-8 w-[calc(100%+4rem)]">
      {hasValidWidth && (
        <Carousel.Root
          // Reset to page 0 when visibleCards changes
          key={visibleCards}
          className={cx('flex flex-col items-center gap-5', className)}
        >
          <CalloutCarouselContent
            onDismiss={onDismiss}
            onLoadMore={onLoadMore}
            showLoadMore={showLoadMore}
            isLoadingMore={isLoadingMore}
            pages={pages}
            containerWidth={containerWidth}
            edgePadding={EDGE_PADDING}
            cardGap={CARD_GAP}
            getCardWidthForPage={getCardWidthForPage}
            onCalloutClick={onCalloutClick}
          />
        </Carousel.Root>
      )}
    </div>
  );
};

export default CalloutCarouselBase;
